Ext.define('SA.view.TableRenderer', {
    constructor({borderSizes, cellDelegate, corner, verticalScrollbarCap, horizontalScrollbarCap, topHeaderRenderer, leftHeaderRenderer, bodyRenderer, getBodyScrollPosition}) {
        this.borderSizes = borderSizes;
        this.cellDelegate = cellDelegate;
        this.corner = corner;
        this.verticalScrollbarCap = verticalScrollbarCap;
        this.horizontalScrollbarCap = horizontalScrollbarCap;
        this.topHeaderRenderer = topHeaderRenderer;
        this.leftHeaderRenderer = leftHeaderRenderer;
        this.bodyRenderer = bodyRenderer;
        this.getBodyScrollPosition = getBodyScrollPosition;
    },

    render(state) {
        if (!state.hasTrackIds()) {
            return;
        }

        var dragColId = state.getDragColId(),
            trackIds = state.getTrackIds(),
            containerSize = state.getContainerSize(),
            borderSizes = this.borderSizes,
            scrollbarSize = Ext.getScrollbarSize(),
            scrollbarWidth = scrollbarSize.width,
            scrollbarHeight = scrollbarSize.height,
            trackContentSizes = this.resolveTrackContentSizes({trackIds}),
            topHeaderHeight = trackContentSizes.getRowHeight({rowId: TABLE_HEADER_TRACK_IDS.TOP_HEADER_ROW}) + (2 * borderSizes.getHeight()), 
            leftHeaderWidth = trackContentSizes.getColWidth({colId: TABLE_HEADER_TRACK_IDS.LEFT_HEADER_COL}) + (2 * borderSizes.getWidth()),  
            bodyRegions = this.resolveBodyRegions({borderSizes, trackIds, trackContentSizes}),
            intrinsicBodySize = bodyRegions.getSize(),
            intrinsicBodyWidth = intrinsicBodySize.width,
            intrinsicBodyHeight = intrinsicBodySize.height,
            constrainedBodySize = this.resolveConstrainedBodySize({containerSize, intrinsicBodySize, leftHeaderWidth, topHeaderHeight, scrollbarSize}),
            constrainedBodyWidth = constrainedBodySize.width,
            constrainedBodyHeight = constrainedBodySize.height,
            scrollRegion = this.resolveScrollRegion({scrollPosition: this.getBodyScrollPosition(), constrainedBodySize}),
            bodyScrollFrame = bodyRegions.intersectRegion(scrollRegion),
            bodyScrollFrameRowIds = bodyScrollFrame.getRowIds(),
            bodyScrollFrameColIds = bodyScrollFrame.getColIds(),
            bodyScrollFramePosition = bodyScrollFrame.getPosition(),
            needsVerticalScrollbarCap = (scrollbarWidth > 0) && (intrinsicBodyHeight > constrainedBodyHeight),
            needsHorizontalScrollbarCap = (scrollbarHeight > 0) && (intrinsicBodyWidth > constrainedBodyWidth),
            verticalScrollbarCapWidth = needsVerticalScrollbarCap ? scrollbarWidth : 0,
            horizontalScrollbarCapHeight = needsHorizontalScrollbarCap ? scrollbarHeight : 0,
            topHeaderWidth = needsVerticalScrollbarCap ? (constrainedBodyWidth - scrollbarWidth) : constrainedBodyWidth,
            leftHeaderHeight = needsHorizontalScrollbarCap ? (constrainedBodyHeight - scrollbarHeight) : constrainedBodyHeight;

        this.corner.setSize({width: leftHeaderWidth, height: topHeaderHeight});
        this.verticalScrollbarCap.setSize({width: verticalScrollbarCapWidth, height: topHeaderHeight});
        this.horizontalScrollbarCap.setSize({width: leftHeaderWidth, height: horizontalScrollbarCapHeight});

        this.topHeaderRenderer.render({
            dragColId,
            trackContentSizes,
            containerSize: {width: topHeaderWidth, height: topHeaderHeight},
            scrollFillerSize: {width: intrinsicBodyWidth, height: topHeaderHeight},
            scrollFrameOffset: {top: 0, left: bodyScrollFramePosition.left},
            scrollFrameTrackIds: {rowIds: [TABLE_HEADER_TRACK_IDS.TOP_HEADER_ROW], colIds: bodyScrollFrameColIds}
        });

        this.leftHeaderRenderer.render({
            dragColId,
            trackContentSizes,
            containerSize: {width: leftHeaderWidth, height: leftHeaderHeight},
            scrollFillerSize: {width: leftHeaderWidth, height: intrinsicBodyHeight},
            scrollFrameOffset: {top: bodyScrollFramePosition.top, left: 0},
            scrollFrameTrackIds: {rowIds: bodyScrollFrameRowIds, colIds: [TABLE_HEADER_TRACK_IDS.LEFT_HEADER_COL]}
        });

        this.bodyRenderer.render({
            dragColId,
            trackContentSizes,
            containerSize: constrainedBodySize,
            scrollFillerSize: intrinsicBodySize,
            scrollFrameOffset: bodyScrollFramePosition,
            scrollFrameTrackIds: {rowIds: bodyScrollFrameRowIds, colIds: bodyScrollFrameColIds}
        });
    },

    resolveBodyRegions({borderSizes, trackIds, trackContentSizes}) {
        var getVerticalBorder = () => ({colId: null, width: borderSizes.getWidth()}),
            getHorizontalBorder = () => ({rowId: null, height: borderSizes.getHeight()}),
            prevRegionBottom = 0,
            prevRegionRight = 0,
            rowRegions = trackIds.getRowIds()
                .map(rowId => ({rowId, height: trackContentSizes.getRowHeight({rowId})}))
                .flatMap(heightItem => [getHorizontalBorder(), heightItem]).concat([getHorizontalBorder()]) 
                .map(({rowId, height}) => {
                    var top = prevRegionBottom,
                        bottom = prevRegionBottom = (top + height);

                    return new SA.view.TableRowRegion({rowId, top, bottom});
                })
                .filter(region => region.getRowId() !== null),
            colRegions = trackIds.getColIds()
                .map(colId => ({colId, width: trackContentSizes.getColWidth({colId})}))
                .flatMap(widthItem => [getVerticalBorder(), widthItem]).concat([getVerticalBorder()]) 
                .map(({colId, width}) => {
                    var left = prevRegionRight,
                        right = prevRegionRight = (left + width);

                    return new SA.view.TableColRegion({colId, left, right});
                })
                .filter(region => region.getColId() !== null);

        return new SA.view.TableBodyRegions({borderSizes, rowRegions, colRegions});
    },

    resolveConstrainedBodySize({containerSize, intrinsicBodySize: contentSize, leftHeaderWidth, topHeaderHeight, scrollbarSize}) {
        var contentWidthPlusScrollbarWidth = contentSize.width + scrollbarSize.width,
            contentHeightPlusScrollbarHeight = contentSize.height + scrollbarSize.height,
            containerSize = {width: (containerSize.width - leftHeaderWidth), height: (containerSize.height - topHeaderHeight)};

        return SA.view.ConstrainedSizeResolver.getConstrainedSize({containerSize, contentSize, contentWidthPlusScrollbarWidth, contentHeightPlusScrollbarHeight});
    },

    resolveScrollRegion({scrollPosition: {top, left}, constrainedBodySize: {width, height}}) {
        var right = left + width,
            bottom = top + height;

        return SA.view.Region.create({top, left, right, bottom});
    },

    resolveTrackContentSizes({trackIds}) {
        var rowIds = [TABLE_HEADER_TRACK_IDS.TOP_HEADER_ROW, ...trackIds.getRowIds()],
            colIds = [TABLE_HEADER_TRACK_IDS.LEFT_HEADER_COL, ...trackIds.getColIds()],
            idTuples = rowIds.flatMap(rowId => colIds.map(colId => [rowId, colId])),
            rowHeights = {},
            colWidths = {};

        idTuples.forEach(([rowId, colId]) => {
            var cellContentSize = this.cellDelegate.getCellContentSize({rowId, colId});

            rowHeights[rowId] = Math.max(cellContentSize.height, rowHeights[rowId] ?? 0);
            colWidths[colId] = Math.max(cellContentSize.width, colWidths[colId] ?? 0);
        });

        return new SA.view.TableTrackContentSizes({rowHeights, colWidths});
    }
});